import axios from "axios";
import ResponseAlerts from "../functions/ResponseAlerts";

const BASE_LICENCE = "access-control-service/licenses";

const RESOURCES = {
	GET_CLIENT_LICENCE: (clientId) => `${BASE_LICENCE}/organization/${clientId}`,
	GET_CLIENT_ALLOCATED_LICENCE: (clientId) => `${BASE_LICENCE}/organization/${clientId}/allocated`,
	GET_CONDOMINIUM_LICENCES: (condominiumId) =>
		`${BASE_LICENCE}/allocated-units/${condominiumId}/access-points`,
	LIST_LICENCES_PAGINATED: (page, searchText, orderBy) =>
		`${BASE_LICENCE}/paginated?page=${page}&limit=8&searchBy=${searchText}&orderBy=${orderBy.collumnTitle}&orderDirection=${orderBy.orderDirection}`,
	LIST_LICENCES_FILTERS_PAGINATED: (page, filters, orderBy, rowsPerPage) => {
		const params = {
			page,
			searchBy: filters.search ?? "",
			orderBy: orderBy.collumnTitle,
			orderDirection: orderBy.orderDirection,
			limit: rowsPerPage,
		};
		const searchParams = new URLSearchParams(params);
		return `${BASE_LICENCE}/paginated?${searchParams.toString()}`;
	},
	GET_LICENCE_CONDOMINIO: (clientId) =>
		`aggregates/licences/condominiums/clients/${clientId}`,
	DELETE_CLIENT_LICENCE: (licenceId) => `${BASE_LICENCE}/${licenceId}`,
	UPDATE_CLIENT_LICENCE: (licenceId) => `${BASE_LICENCE}/${licenceId}`,
	UPDATE_CONDOMINIUM_LICENCE: (condominiumId) =>
		`${BASE_LICENCE}/condominiums/${condominiumId}`,
	UPDATE_CLIENT_LICENCE_LINK: (clientId) =>
		`${BASE_LICENCE}/clients/${clientId}`,
};

class ClientLicenceService {
	static async listAllLicences() {
		try {
			return await axios.get(BASE_LICENCE);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licenças",
			});
			return error.response;
		}
	}

	static async listClientsLicence(clientId, abortController) {
		try {
			return await axios.get(RESOURCES.GET_CLIENT_LICENCE(clientId), abortController ? {
				signal: abortController.signal,
			} : null);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licença",
			});
			return error.response;
		}
	}

	static async listClientsAllocatedLicence(clientId, abortController) {
		try {
			return await axios.get(RESOURCES.GET_CLIENT_ALLOCATED_LICENCE(clientId), abortController ? {
				signal: abortController.signal,
			} : null);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licença",
			});
			return error.response;
		}
	}

	static async listCondominiumLicencesAndAccess(condominiumId) {
		try {
			return await axios.get(RESOURCES.GET_CONDOMINIUM_LICENCES(condominiumId));
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licenças",
			});
			return error.response;
		}
	}

	static async listLicencesPaginated(page, searchText, orderBy) {
		try {
			return await axios.get(
				RESOURCES.LIST_LICENCES_PAGINATED(page, searchText, orderBy),
			);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licenças",
			});
			return error.response;
		}
	}

	static async listLicencesFiltersPaginated(page, filters, orderBy, rowsPerPage, cancelToken) {
		try {
			return await axios.get(
				RESOURCES.LIST_LICENCES_FILTERS_PAGINATED(page, filters, orderBy, rowsPerPage),
				{
					signal: cancelToken
				}
			);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licenças",
			});
			return error.response;
		}
	}

	static async listLicencesAndCondominios(clientId) {
		try {
			return await axios.get(RESOURCES.GET_LICENCE_CONDOMINIO(clientId));
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licenças e condomínios",
			});
			return error.response;
		}
	}

	static async createClientLicence(payload) {
		try {
			const response = await axios.post(BASE_LICENCE, payload);
			ResponseAlerts({
				response: response,
				successMessage: "Licença cadastrada com sucesso",
			});
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao cadastrar licença",
			});
			return error.response;
		}
	}

	static async deleteClientLicence(licenceId) {
		try {
			const response = await axios.delete(
				RESOURCES.DELETE_CLIENT_LICENCE(licenceId),
			);
			ResponseAlerts({
				response: response,
				successMessage: "Licença excluída com sucesso",
			});
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao excluir licença",
			});
			return error.response;
		}
	}

	static async updateClientLicence(licenceId, payload) {
		return await axios
			.put(`${BASE_LICENCE}/${licenceId}`, payload)
			.then((response) => {
				ResponseAlerts({
					response: response,
					successMessage: "Licença atualizada com sucesso",
				});
				return response;
			})
			.catch((error) => {
				console.log(error);
				ResponseAlerts({
					response: error.response,
					errorMessage: "Erro ao atualizar licença",
				});
				return error.response;
			});
	}

	static async updateSNEquipment(licenceId, payload) {
		return await axios
			.put(`${BASE_LICENCE}/update-sn-equipment/${licenceId}`, payload)
			.then((response) => {
				ResponseAlerts({
					response: response,
					successMessage: "Licença atualizada com sucesso",
				});
				return response;
			})
			.catch((error) => {
				console.log(error);
				ResponseAlerts({
					response: error.response,
					errorMessage: "Erro ao atualizar licença",
				});
				return error.response;
			});
	}

	static async updateOwner(licenceId, ownerId) {
		return await axios
			.patch(`${BASE_LICENCE}/owner/${licenceId}`, {
				idOwner: Number(ownerId),
			})
			.then((response) => {
				ResponseAlerts({
					response: response,
					successMessage: "Licença atualizada com sucesso",
				});
				return response;
			})
			.catch((error) => {
				console.log(error);
				ResponseAlerts({
					response: error.response,
					errorMessage: "Erro ao atualizar proprietário da licença",
				});
				return error.response;
			});
	}

	static async updateLicencesLink(idCondominium, licencas, licencasExcluidas) {
		const payload = { licences: licencas, deletedLicences: licencasExcluidas };
		return axios
			.patch(RESOURCES.UPDATE_CONDOMINIUM_LICENCE(idCondominium), payload)
			.then((response) => {
				ResponseAlerts({
					response: response,
					successMessage: "Licenças atualizadas com sucesso",
				});
				return response;
			})
			.catch((error) => {
				console.log(error);
				ResponseAlerts({
					response: error.response,
					errorMessage: "Erro ao vincular licenças",
				});
				return error.response;
			});
	}

	static async updateClientLicenceLink(idClient, licencas, licencasExcluidas) {
		const payload = { licences: licencas, deletedLicences: licencasExcluidas };
		return axios
			.patch(RESOURCES.UPDATE_CLIENT_LICENCE_LINK(idClient), payload)
			.then((response) => {
				ResponseAlerts({
					response: response,
					successMessage: "Licenças vinculadas com sucesso",
				});
				return response;
			})
			.catch((error) => {
				console.log(error);
				ResponseAlerts({
					response: error.response,
					errorMessage: "Erro ao vincular licenças",
				});
				return error.response;
			});
	}
}

export default ClientLicenceService;
